// @ts-check
(function btTastytradeApiServiceClosure() {
  'use strict';

  angular.module('ecapp').factory('btTastytradeApiService', btTastytradeApiService);

  btTastytradeApiService.$inject = [
    '$q',
    '$http',
    'btSettings',
    'btOauthService',
    'btTemplateApiService',
    'btMarketApiService',
    'btInstrumentsService',
    'btSymbolPrefix',
    'BT',
    'btToastrService',
    '$timeout',
    'LoopBackAuth',
  ];

  /**
   * This factory works with tastytrade API: login, accounts, balances, positions and stock orders.
   * BetterTrader's instruments, prices and charts stay as with no broker connected; tastytrade's own stocks
   * (bare tickers such as `AAPL`) are added next to them and are the ones that can be traded.
   *
   * @ngdoc service
   * @name btTastytradeApiService
   * @memberOf ecapp
   * @param {angular.IQService} $q
   * @param {angular.IHttpService} $http
   * @param {ecapp.ISettings} btSettings
   * @param {ecapp.IOauthService} btOauthService
   * @param {ecapp.ITemplateApiService} btTemplateApiService
   * @param {ecapp.IMarketApiService} btMarketApiService
   * @param {ecapp.IInstrumentsService} btInstrumentsService
   * @param {ecapp.ISymbolPrefix} btSymbolPrefix
   * @param {ecapp.IBTConstants} BT
   * @param {ecapp.IToastrService} btToastrService
   * @param {angular.ITimeoutService} $timeout
   * @param {ecapp.ILoopBackAuth} LoopBackAuth - BetterTrader session, sent with token refreshes
   * @return {ecapp.ITastytradeApiService}
   */
  function btTastytradeApiService(
    $q,
    $http,
    btSettings,
    btOauthService,
    btTemplateApiService,
    btMarketApiService,
    btInstrumentsService,
    btSymbolPrefix,
    BT,
    btToastrService,
    $timeout,
    LoopBackAuth
  ) {
    console.log('Running btTastytradeApiService');

    const BROKER = 'tastytrade';
    const TICKER_PREFIX = 'TASTYTRADE';
    const COMING_SOON = 'Coming soon!';
    // The exact text btTradingService.isReLoginNeeded matches, so the user gets its "Please log in again" popup.
    const SESSION_EXPIRED = 'Access token expired - try to login again';
    const NO_ACCOUNT = 'Select a tastytrade account first';

    const MINUTE_MS = 60 * 1000;
    // Candles kept per stock and period.
    const CANDLE_CACHE_SIZE = 500;
    // OANDA-style granularity used by BetterTrader charts -> dxFeed candle period and its length.
    const CANDLE_GRANULARITIES = {
      M1: { period: 'm', ms: MINUTE_MS },
      M5: { period: '5m', ms: 5 * MINUTE_MS },
      M10: { period: '10m', ms: 10 * MINUTE_MS },
      M15: { period: '15m', ms: 15 * MINUTE_MS },
      M30: { period: '30m', ms: 30 * MINUTE_MS },
      H1: { period: 'h', ms: 60 * MINUTE_MS },
      H4: { period: '4h', ms: 240 * MINUTE_MS },
      D: { period: 'd', ms: 1440 * MINUTE_MS },
      W: { period: 'w', ms: 7 * 1440 * MINUTE_MS },
      M: { period: 'mo', ms: 31 * 1440 * MINUTE_MS },
    };

    // Extra advice for rejections a user can fix right away; tastytrade's own text says only what is wrong.
    const ERROR_HINTS = {
      tif_no_after_hours_opening_market_orders:
        'Market and Stop orders work only while the US market is open (9:30-16:00 New York); use a Limit order to queue it for the open.',
    };

    // tastytrade status -> the app's `status` (its filters and hasOrders rely on these values) and the shown label.
    const ORDER_STATUSES = {
      Received: { status: 'Received', text: 'Received' },
      Routed: { status: 'Received', text: 'Sent' },
      'In Flight': { status: 'Received', text: 'Sent' },
      Live: { status: 'Received', text: 'Working' },
      Contingent: { status: 'Received', text: 'Waiting' },
      'Cancel Requested': { status: 'Received', text: 'Cancelling' },
      'Replace Requested': { status: 'Received', text: 'Updating' },
      Filled: { status: 'Filled', text: 'Filled' },
      Cancelled: { status: 'Rejected', text: 'Cancelled' },
      Expired: { status: 'Rejected', text: 'Expired' },
      Rejected: { status: 'Rejected', text: 'Rejected' },
      Removed: { status: 'Rejected', text: 'Removed' },
      'Partially Removed': { status: 'Rejected', text: 'Partially removed' },
    };

    // A DELETE only requests the cancel; cancelOrder re-reads the order every CANCEL_CHECK_MS, up to CANCEL_CHECKS times.
    const CANCEL_CHECK_MS = 1000;
    const CANCEL_CHECKS = 5;
    const CANCEL_UNCONFIRMED = 'tastytrade has not confirmed the cancel yet - check the order list in a moment';

    const ORDER_TYPES = {
      [BT.ORDER_TYPE.MARKET]: 'Market',
      [BT.ORDER_TYPE.LIMIT]: 'Limit',
      [BT.ORDER_TYPE.STOP]: 'Stop',
    };

    const BASE_URLS = {
      demo: 'https://api.cert.tastyworks.com',
      real: 'https://api.tastyworks.com',
    };

    // Refresh this long before the 15-minute access token actually expires.
    const EXPIRY_MARGIN_MS = 60 * 1000;

    /** @type {ecapp.ITastytradeAuthData | null} */
    let gUserData = null;
    /** @type {ecapp.ICallbackWithData<ecapp.ITastytradeAuthData> | null} */
    let gSaveData = null;
    /** @type {angular.IPromise<void> | null} */
    let gRefreshPromise = null;
    /** @type {ecapp.ITradingAccount[]} */
    let gAccounts = [];
    /** @type {string | null} */
    let gSelectedAccountId = null;
    /** @type {Record<string, angular.IPromise<ecapp.ITradingInstrument>>} */
    let gSymbolCache = {};
    /** @type {ecapp.ITastytradeDxLink | null} opened on first use */
    let gDxLink = null;
    /** @type {{ token: string, url: string, expiresAt: number } | null} */
    let gQuoteToken = null;
    /** @type {Record<string, { candles: Array<*>, request: angular.IPromise<void> | null }>} per stock and period */
    let gCandleCache = {};
    /** @type {ecapp.ITastytradeAuthData | null} the session tastytrade refused to refresh */
    let gRefusedSession = null;

    // --- Session ---

    /**
     * @param {ecapp.ITastytradeAuthData} data - access data saved in `brokerData`
     * @param {ecapp.ICallbackWithData<ecapp.ITastytradeAuthData>} saveDataFunction - persists refreshed tokens
     * @return {angular.IPromise<*>}
     */
    function initialize(data, saveDataFunction) {
      gSaveData = saveDataFunction || null;

      if (!data || !data.refreshToken) return $q.reject(new Error('Bad access data'));

      gUserData = data;
      gSelectedAccountId = data.defaultAccount || null;
      return $q.resolve({});
    }

    /**
     * @return {angular.IPromise<ecapp.ITastytradeAuthData>}
     */
    function getAccessData() {
      return $q.resolve(gUserData);
    }

    /**
     * @return {angular.IPromise<*>}
     */
    function connect() {
      return gUserData ? $q.resolve({}) : $q.reject(new Error('Not logged in to tastytrade'));
    }

    /**
     * @param {string} mode - ignored: the server's `TASTYTRADE_MODE` decides and returns it with the tokens
     * @param {boolean} isForceLogin - unused
     * @param {ecapp.ICallbackWithData<ecapp.ITastytradeAuthData>} [saveDataFunction] - persists refreshed tokens
     * @return {angular.IPromise<ecapp.ITastytradeAuthData>}
     */
    function login(mode, isForceLogin, saveDataFunction) {
      if (saveDataFunction) gSaveData = saveDataFunction;

      return btOauthService.login('tastytrade').then(function (data) {
        _resetSession();
        gUserData = {
          mode: data.mode,
          token: data.accessToken,
          refreshToken: data.refreshToken,
          expiresAt: _getExpiresAt(data.expiresIn),
        };
        return gUserData;
      });
    }

    /**
     * @return {angular.IPromise<*>}
     */
    function logout() {
      _resetSession();
      return $q.resolve({});
    }

    /**
     * Forgets everything tied to the current session; a new login may be in the other environment.
     *
     * @private
     */
    function _resetSession() {
      gUserData = null;
      gRefreshPromise = null;
      gRefusedSession = null;
      gAccounts = [];
      gSelectedAccountId = null;
      gSymbolCache = {};
      gQuoteToken = null;
      gCandleCache = {};
      if (gDxLink) gDxLink.close();
      gDxLink = null;
    }

    /**
     * @return {boolean}
     */
    function isLoggedIn() {
      return gUserData !== null;
    }

    /**
     * @return {ecapp.TastytradeMode}
     */
    function getTradingMode() {
      return gUserData.mode;
    }

    /**
     * @param {ecapp.TastytradeMode} mode
     * @return {boolean} trading mode was changed
     */
    function setTradingMode(mode) {
      gUserData.mode = mode;
      return true;
    }

    // --- Accounts ---

    /**
     * @return {angular.IPromise<ecapp.ITradingAccount[]>}
     */
    function getAccounts() {
      return _request('/customers/me/accounts').then(function (data) {
        gAccounts = data.items
          .filter(function (item) {
            return !item.account['is-closed'];
          })
          .map(_convertAccount);
        gSelectedAccountId = btTemplateApiService.resetSelectedAccount(gAccounts, gSelectedAccountId);
        return gAccounts;
      });
    }

    /**
     * @param {string[]} accountIds - account numbers
     * @return {angular.IPromise<ecapp.ITradingBalance[]>}
     */
    function getBalances(accountIds) {
      return $q.all(
        accountIds.map(function (accountId) {
          return _request('/accounts/' + accountId + '/balances').then(_convertBalance);
        })
      );
    }

    /**
     * @param {string[]} accountIds - account numbers
     * @return {angular.IPromise<ecapp.ITradingPosition[]>}
     */
    function getPositions(accountIds) {
      return $q
        .all(
          accountIds.map(function (accountId) {
            // Marks are off by default; without them there is no current price to compute P&L from.
            return _request('/accounts/' + accountId + '/positions', { 'include-marks': true }).then(function (data) {
              return data.items.map(_convertPosition);
            });
          })
        )
        .then(_flatten);
    }

    /**
     * @param {string[]} accountIds - account numbers
     * @return {angular.IPromise<ecapp.ITradingOrder[]>}
     */
    function getOrders(accountIds) {
      return $q
        .all(
          accountIds.map(function (accountId) {
            return _request('/accounts/' + accountId + '/orders/live').then(function (data) {
              return data.items.map(_convertOrder);
            });
          })
        )
        .then(_flatten);
    }

    /**
     * @param {string} id - account number
     * @return {string | null} selected account number; btTradingService persists it as `defaultAccount`
     */
    function selectAccount(id) {
      const isKnown = gAccounts.some(function (item) {
        return item.key === id;
      });

      gSelectedAccountId = isKnown ? id : null;
      return gSelectedAccountId;
    }

    /**
     * @return {string | null}
     */
    function getSelectedAccountId() {
      return gSelectedAccountId;
    }

    /**
     * @param {string} id - account number
     * @return {boolean}
     */
    function isAccountSelected(id) {
      return gSelectedAccountId === id;
    }

    // --- Instruments: BetterTrader's feed, plus tastytrade's own stocks ---

    /**
     * Only stocks trade on tastytrade under the very symbol that is shown: its own and BetterTrader's `ST:` ones.
     *
     * @param {ecapp.ITradingInstrument} instrument
     * @return {boolean}
     */
    function checkTradable(instrument) {
      return !!instrument && _getStockSymbol(instrument.brokerSymbol) !== null;
    }

    /**
     * Positions and orders carry the bare ticker, while a BetterTrader `ST:AAPL` card trades the same stock.
     *
     * @param {string} brokerSymbol - of a position or order
     * @param {string} symbol - of a card
     * @return {boolean}
     */
    function checkSameSymbol(brokerSymbol, symbol) {
      const stock = _getStockSymbol(brokerSymbol);
      return stock !== null && stock === _getStockSymbol(symbol);
    }

    /**
     * @param {string} ticker - chart or datafeed ticker
     * @return {boolean} whether it is one of tastytrade's own stocks, charted over DXLink
     */
    function checkTastytradeTicker(ticker) {
      return (
        String(ticker || '')
          .toUpperCase()
          .indexOf(TICKER_PREFIX + ':') === 0
      );
    }

    /**
     * @param {*} udf - BetterTrader's UDF datafeed, kept for its own instruments
     * @return {*} TradingView datafeed
     */
    function createDatafeed(udf) {
      return new window.TastytradeDataFeed(udf, service);
    }

    /**
     * @return {angular.IPromise<boolean>} whether this server has tastytrade's OAuth client (oauth-tastytrade.js);
     *   rejects when the server could not be asked, so a network blip is not read as "disabled"
     */
    function checkLoginEnabled() {
      return $http.get(btSettings.BT_BACKEND_URL + '/auth/tastytrade/status').then(function (response) {
        return !!(response.data && response.data.isEnabled);
      });
    }

    /**
     * @param {string} ticker - `TASTYTRADE:AAPL` or bare `AAPL` for tastytrade, anything else for BetterTrader's feed
     * @return {angular.IPromise<ecapp.ITradingInstrument>}
     */
    function getSymbolInfo(ticker) {
      const symbol = _getOwnSymbol(ticker);
      if (!symbol) return btMarketApiService.getSymbolInfo(ticker);

      if (!gSymbolCache[symbol]) {
        gSymbolCache[symbol] = _request('/instruments/equities/' + encodeURIComponent(symbol))
          .then(function (equity) {
            return _createOwnInstrument(equity.symbol, equity.description, equity);
          })
          .catch(function (error) {
            delete gSymbolCache[symbol];
            return $q.reject(error);
          });
      }

      return gSymbolCache[symbol];
    }

    /**
     * Search for "+ add to watchlist": BetterTrader's results first, then tastytrade's stocks.
     *
     * @param {string} text - search text
     * @param {number} limit - maximum number of results per source
     * @param {Object} params - search parameters
     * @return {angular.IPromise<ecapp.ITradingSymbolSuggestion[]>}
     */
    function suggestSymbols(text, limit, params) {
      // Either source failing must not hide the other's results.
      const own = _request('/instruments/search', { query: text, limit: limit || 10 })
        .then(function (data) {
          return data.items
            .filter(function (item) {
              // A symbol like `AAPL:BOSS` would be taken for one of BetterTrader's and sent to its feed.
              return item['instrument-type'] === 'Equity' && !btInstrumentsService.checkBetterTraderSymbol(item.symbol);
            })
            .map(_convertSearchResult);
        })
        .catch(function (error) {
          console.warn('tastytrade symbol search failed:', error && error.message);
          return [];
        });
      const shared = btMarketApiService.suggestSymbols(text, limit, params).catch(function (error) {
        console.warn('BetterTrader symbol search failed:', error && error.message);
        return [];
      });

      return $q.all([shared, own]).then(_flatten);
    }

    /**
     * Fallback when a symbol is not one of BetterTrader's instruments (btTradingService.getInstrumentByBrokerSymbol),
     * e.g. a position's `AAPL`.
     *
     * @param {string} brokerSymbol
     * @return {ecapp.ITradingInstrument}
     */
    function createInstrument(brokerSymbol) {
      const symbol = _getOwnSymbol(brokerSymbol);
      if (symbol) return _createOwnInstrument(symbol, symbol, {});
      // Same placeholder the unconnected app makes for an unknown symbol.
      return btInstrumentsService.createBrokerInstrument('default', brokerSymbol, 'oanda', 'Unknown', true);
    }

    // --- Orders (equities only) ---

    /**
     * Dry-runs the order: tastytrade validates it and returns the buying-power effect without placing it.
     *
     * @param {ecapp.ITradingOrderRequest} order
     * @return {angular.IPromise<ecapp.ITradingOrderConfirmation>}
     */
    function confirmOrder(order) {
      return _prepareOrder(order).then(function (body) {
        return _request('/accounts/' + gSelectedAccountId + '/orders/dry-run', undefined, 'POST', body);
      });
    }

    /**
     * @param {ecapp.ITradingOrderRequest} order
     * @return {angular.IPromise<ecapp.ITradingOrderResponse>}
     */
    function submitOrder(order) {
      return _prepareOrder(order)
        .then(function (body) {
          return _request('/accounts/' + gSelectedAccountId + '/orders', undefined, 'POST', body);
        })
        .then(function (data) {
          const warnings = _joinMessages(data.warnings);
          // btTradingService.processOrder only says "order processed"; this is e.g. "begins working next session".
          if (warnings) btToastrService.info(warnings, order.symbol + ' order', { type: 'trade' });
          return { key: String(data.order.id), msg: warnings };
        });
    }

    /**
     * @param {string} orderId - tastytrade order id
     * @return {angular.IPromise<ecapp.ITradingOrderResponse>}
     */
    function cancelOrder(orderId) {
      if (!gSelectedAccountId) return $q.reject(new Error(NO_ACCOUNT));

      const path = '/accounts/' + gSelectedAccountId + '/orders/' + encodeURIComponent(orderId);
      return _request(path, undefined, 'DELETE').then(function (order) {
        return _waitForCancel(path, order, CANCEL_CHECKS);
      });
    }

    /**
     * The venue can still refuse a requested cancel or fill the order first; the sandbox refuses some pre-market.
     *
     * @param {string} path - the order's API path
     * @param {Object} order - tastytrade order as last read
     * @param {number} checksLeft
     * @return {angular.IPromise<ecapp.ITradingOrderResponse>}
     * @private
     */
    function _waitForCancel(path, order, checksLeft) {
      const status = order && order.status;
      if (status === 'Cancelled' || status === 'Expired' || status === 'Removed') {
        return $q.resolve({ key: String(order.id) });
      }

      if (!status || status === 'Cancel Requested') {
        if (checksLeft <= 0) return $q.reject(new Error(CANCEL_UNCONFIRMED));

        return $timeout(CANCEL_CHECK_MS)
          .then(function () {
            return _request(path);
          })
          .then(
            function (next) {
              return _waitForCancel(path, next, checksLeft - 1);
            },
            function () {
              return $q.reject(new Error(CANCEL_UNCONFIRMED));
            }
          );
      }

      return $q.reject(
        new Error(
          'tastytrade did not cancel the order - it is ' +
            (ORDER_STATUSES[status] ? ORDER_STATUSES[status].text : status)
        )
      );
    }

    /**
     * Converts a BetterTrader order request into a tastytrade order body.
     *
     * @param {ecapp.ITradingOrderRequest} order
     * @return {angular.IPromise<Object>}
     * @private
     */
    function _prepareOrder(order) {
      if (!gSelectedAccountId) return $q.reject(new Error(NO_ACCOUNT));

      const orderType = ORDER_TYPES[order.type];
      const quantity = Number(order.quantity);
      const stopLoss = order.stopLoss && typeof order.stopLoss === 'object' ? order.stopLoss.price : order.stopLoss;
      // The ticket sends a stop order's trigger as its price (bt-instrument-prices.js, non-TradeStation branch).
      const trigger = orderType === 'Stop' ? Number(order.limitPrice) : 0;

      if (!orderType) return $q.reject(new Error('Order type ' + order.type + ' is not supported for tastytrade'));
      if (!(quantity > 0)) return $q.reject(new Error('Quantity must be greater than 0'));
      if (orderType === 'Limit' && !(Number(order.limitPrice) > 0)) return $q.reject(new Error('Enter a limit price'));
      if (orderType === 'Stop' && !(trigger > 0)) return $q.reject(new Error('Enter a stop price'));
      if (order.takeProfit || stopLoss) {
        // Rejected rather than dropped, so nobody believes a protective order exists when it does not.
        return $q.reject(new Error('Take profit and stop loss are not supported for tastytrade yet'));
      }

      const symbol = _getStockSymbol(order.symbol);
      if (!symbol) return $q.reject(new Error('Only US stocks and ETFs can be traded with tastytrade'));

      // tastytrade wants "to Open" or "to Close", so the current position decides which.
      return _request('/accounts/' + gSelectedAccountId + '/positions').then(function (data) {
        const position = data.items.find(function (item) {
          return item.symbol === symbol;
        });
        const held = position
          ? _toNumber(position.quantity) * (position['quantity-direction'] === 'Short' ? -1 : 1)
          : 0;
        // _correctOrder may set SELL_SHORT/BUY_COVER, which BT.TRADE_ACTION lacks; only BUY is a buy, the position decides open/close.
        const isBuy =
          order.action === BT.TRADE_ACTION.BUY ||
          (!!BT.TRADE_ACTION.BUY_COVER && order.action === BT.TRADE_ACTION.BUY_COVER);
        const isClosing = isBuy ? held < 0 : held > 0;

        if (isClosing && quantity > Math.abs(held)) {
          return $q.reject(
            new Error(
              'Quantity ' +
                quantity +
                ' is more than your ' +
                symbol +
                ' position of ' +
                Math.abs(held) +
                '. Close the position first, then open a new one.'
            )
          );
        }

        const timeInForce = _getTimeInForce(order, orderType);
        const body = {
          'order-type': orderType,
          'time-in-force': timeInForce.value,
          legs: [
            {
              'instrument-type': 'Equity',
              symbol: symbol,
              quantity: String(quantity),
              action: (isBuy ? 'Buy' : 'Sell') + (isClosing ? ' to Close' : ' to Open'),
            },
          ],
        };

        if (orderType === 'Limit') {
          body.price = String(order.limitPrice);
          body['price-effect'] = isBuy ? 'Debit' : 'Credit';
        }
        if (orderType === 'Stop') body['stop-trigger'] = String(trigger);

        return body;
      });
    }

    /**
     * The ticket offers tastytrade's own Day / GTC (bt-instrument-prices.js getDurationOptions); market orders are Day.
     *
     * @param {ecapp.ITradingOrderRequest} order
     * @param {string} orderType - tastytrade order type
     * @return {{ value: string }}
     * @private
     */
    function _getTimeInForce(order, orderType) {
      return { value: orderType !== 'Market' && order.timeInForce === 'GTC' ? 'GTC' : 'Day' };
    }

    // --- Market data: BetterTrader's feed, and DXLink for tastytrade's own stocks ---

    /**
     * @param {string} symbol - price key: BetterTrader ticker, or a tastytrade stock's bare ticker
     * @return {angular.IPromise<ecapp.ITradingLiveCandle>}
     */
    function getLiveCandleData(symbol) {
      const own = _getOwnSymbol(symbol);
      if (!own) return btMarketApiService.getLiveCandleData(symbol);

      // $q.when: the stream resolves outside Angular, and cards only redraw inside a digest.
      return $q.when(getDxLink().watchQuote(own)).then(function (quote) {
        return _convertLiveCandle(symbol, quote);
      });
    }

    /**
     * Recent candles for the instrument page chart, in BetterTrader's history shape.
     *
     * @param {string} symbol - price key
     * @param {string} granularity - OANDA style: `M1`, `M5`, `M15`, `H1`, `D`, ...
     * @param {number} [count] - number of candles
     * @return {angular.IPromise<{ candles: ecapp.ITradingLastCandle[] }>} same shape as btMarketApiService's
     */
    function getLastCandlesData(symbol, granularity, count) {
      const own = _getOwnSymbol(symbol);
      if (!own) return btMarketApiService.getLastCandlesData(symbol, granularity, count);

      const options = CANDLE_GRANULARITIES[granularity];
      if (!options) return $q.reject(new Error('Unsupported granularity ' + granularity));

      const size = count || 30;
      const key = own + '|' + options.period;
      const cache = (gCandleCache[key] = gCandleCache[key] || { candles: [], request: null });
      // The instrument chart polls every 5 s; overlapping polls share one request.
      if (!cache.request) {
        cache.request = _loadCandles(own, options, size, cache).finally(function () {
          cache.request = null;
        });
      }

      return cache.request.then(function () {
        return {
          instrument: symbol,
          granularity: granularity,
          candles: cache.candles.slice(-size).map(function (candle) {
            return {
              complete: candle.time + options.ms <= Date.now(),
              mid: { o: candle.open, h: candle.high, l: candle.low, c: candle.close },
              time: Math.floor(candle.time / 1000),
              volume: candle.volume,
            };
          }),
        };
      });
    }

    /**
     * Fills the cache: wide history the first time, then only from the newest cached candle (still forming) on.
     *
     * @param {string} own - tastytrade streamer symbol
     * @param {{ period: string, ms: number }} options - candle period
     * @param {number} size - candles the caller shows
     * @param {{ candles: Array<*> }} cache
     * @return {angular.IPromise<void>}
     * @private
     */
    function _loadCandles(own, options, size, cache) {
      const newest = cache.candles.length >= size ? cache.candles[cache.candles.length - 1].time : 0;
      // Several times the bare span, so nights and weekends without trading still leave `size` candles.
      const from = newest || Date.now() - Math.max(3 * size * options.ms, 5 * 24 * 3600 * 1000);

      return $q.when(getDxLink().getCandles(own, options.period, from)).then(function (candles) {
        // Nothing back (snapshot timed out) must not cost the cached candles.
        if (!candles.length) return;
        const checkBeforeFrom = function (candle) {
          return candle.time < from;
        };
        cache.candles = cache.candles
          .filter(checkBeforeFrom)
          .concat(
            candles.filter(function (candle) {
              return !checkBeforeFrom(candle);
            })
          )
          .slice(-CANDLE_CACHE_SIZE);
      });
    }

    /**
     * @param {string[]} symbols - price keys
     * @return {angular.IPromise<Array<ecapp.ITradingLiveCandle | undefined>>} in `symbols` order
     */
    function getLiveCandlesData(symbols) {
      const split = _splitSymbols(symbols);
      return $q
        .all([
          split.shared.length ? btMarketApiService.getLiveCandlesData(split.shared).catch(_skipSharedPrices([])) : [],
          _collectAvailable(split.own.map(getLiveCandleData)),
        ])
        .then(function (results) {
          // Back in the caller's order, with a gap for a stock without a price: bt-instruments-container pairs by index.
          /** @type {Record<string, ecapp.ITradingLiveCandle>} */
          const bySymbol = {};
          _flatten(results).forEach(function (candle) {
            // btMarketApiService leaves its own gaps for symbols it has no price for.
            if (candle) bySymbol[candle.symbol] = candle;
          });
          return symbols.map(function (symbol) {
            return bySymbol[symbol];
          });
        });
    }

    /**
     * @param {string[]} symbols - price keys
     * @return {angular.IPromise<ecapp.ITradingQuoteResponse>}
     */
    function getQuotes(symbols) {
      const split = _splitSymbols(symbols);
      // BetterTrader's getQuotes rejects an empty list.
      const shared = split.shared.length
        ? btMarketApiService.getQuotes(split.shared).catch(_skipSharedPrices({ prices: [] }))
        : $q.resolve({ prices: [] });
      const own = _collectAvailable(
        split.own.map(function (symbol) {
          return $q.when(getDxLink().watchQuote(_getOwnSymbol(symbol))).then(function (quote) {
            return _convertQuote(symbol, quote);
          });
        })
      );

      return $q.all([shared, own]).then(function (results) {
        return { prices: results[0].prices.concat(results[1]), time: Math.floor(Date.now() / 1000) };
      });
    }

    /**
     * BetterTrader's feed failing must not drop the tastytrade prices in the same batch, nor the other way round.
     *
     * @param {T} empty - what the batch uses instead
     * @return {function(*): T}
     * @template T
     * @private
     */
    function _skipSharedPrices(empty) {
      return function (error) {
        console.warn('BetterTrader prices unavailable:', error && error.message);
        return empty;
      };
    }

    /**
     * One tastytrade stock failing (feed down, token refused) must not drop BetterTrader's prices in the same batch.
     *
     * @param {Array<angular.IPromise<T>>} promises
     * @return {angular.IPromise<T[]>} the values that arrived
     * @template T
     * @private
     */
    function _collectAvailable(promises) {
      return $q
        .all(
          promises.map(function (promise) {
            return promise.catch(function (error) {
              console.warn('tastytrade price unavailable:', error && error.message);
              return null;
            });
          })
        )
        .then(function (values) {
          return values.filter(Boolean);
        });
    }

    /**
     * @param {string[]} symbols
     * @return {{ shared: string[], own: string[] }} BetterTrader's symbols, and tastytrade's own stocks
     * @private
     */
    function _splitSymbols(symbols) {
      return {
        shared: symbols.filter(function (symbol) {
          return !_getOwnSymbol(symbol);
        }),
        own: symbols.filter(function (symbol) {
          return !!_getOwnSymbol(symbol);
        }),
      };
    }

    /**
     * Same shape as BetterTrader's live candle (bt-market-api-service convertUDFQuote), so cards look alike.
     *
     * @param {string} symbol - price key
     * @param {*} quote - DxQuote
     * @return {ecapp.ITradingLiveCandle}
     * @private
     */
    function _convertLiveCandle(symbol, quote) {
      const last = _firstFinite(quote.last, (quote.bid + quote.ask) / 2, quote.bid, quote.ask, quote.prevClose);
      // Before the session opens dxFeed has no day open/high/low yet; the range then collapses to the last price.
      const open = _firstFinite(quote.open, last);
      const high = _firstFinite(quote.high, last);
      const low = _firstFinite(quote.low, last);
      const prevClose = _firstFinite(quote.prevClose, open);
      const bid = _firstFinite(quote.bid, last);
      const ask = _firstFinite(quote.ask, last);

      return {
        symbol: symbol,
        time: Math.floor((quote.time || Date.now()) / 1000),
        yesterday: { close: prevClose, closeText: _toText(prevClose) },
        today: {
          low: low,
          lowText: _toText(low),
          high: high,
          highText: _toText(high),
          open: open,
          openText: _toText(open),
        },
        now: { bid: bid, bidText: _toText(bid), ask: ask, askText: _toText(ask), last: last, lastText: _toText(last) },
      };
    }

    /**
     * Same shape as BetterTrader's quote items (bt-market-api-service getQuotes).
     *
     * @param {string} symbol - price key
     * @param {*} quote - DxQuote
     * @return {ecapp.ITradingQuote}
     * @private
     */
    function _convertQuote(symbol, quote) {
      const candle = _convertLiveCandle(symbol, quote);
      return /** @type {*} */ ({
        instrument: symbol,
        time: candle.time,
        tradeable: true,
        // Strings, as BetterTrader's feed sends them: the card splits the text into its large and small digits.
        last: candle.now.lastText,
        bids: [{ price: candle.now.bidText, liquidity: 1 }],
        asks: [{ price: candle.now.askText, liquidity: 1 }],
      });
    }

    /**
     * @param {...number} values
     * @return {number} the first finite value, or NaN
     * @private
     */
    function _firstFinite() {
      for (let i = 0; i < arguments.length; i++) {
        if (isFinite(arguments[i])) return arguments[i];
      }
      return NaN;
    }

    /**
     * @param {number} value
     * @return {string}
     * @private
     */
    function _toText(value) {
      return isFinite(value) ? value.toFixed(2) : '';
    }

    // --- Market data over DXLink (charts of tastytrade's own stocks) ---

    /**
     * @return {ecapp.ITastytradeDxLink} shared by all charts
     */
    function getDxLink() {
      if (!gDxLink) gDxLink = new window.TastytradeDxLink(_getQuoteToken);
      return gDxLink;
    }

    /**
     * @param {boolean} [isRefused] - DXLink refused the cached token, so fetch a new one
     * @return {angular.IPromise<{ token: string, url: string }>}
     * @private
     */
    function _getQuoteToken(isRefused) {
      // Valid for 24 hours; renewed EXPIRY_MARGIN_MS early.
      const isValid = gQuoteToken && Date.now() < gQuoteToken.expiresAt - EXPIRY_MARGIN_MS;
      if (isValid && !isRefused) return $q.resolve(gQuoteToken);

      return _request('/api-quote-tokens').then(function (data) {
        gQuoteToken = { token: data.token, url: data['dxlink-url'], expiresAt: Date.parse(data['expires-at']) };
        return gQuoteToken;
      });
    }

    // --- Not supported yet ---

    /**
     * @return {angular.IPromise<*>}
     */
    function comingSoon() {
      return $q.reject(new Error(COMING_SOON));
    }

    // --- HTTP ---

    /**
     * Calls tastytrade with a valid access token, refreshing it first when needed and once more on 401.
     *
     * @param {string} path - API path
     * @param {Object} [params] - query parameters
     * @param {string} [method] - HTTP method, `GET` by default
     * @param {Object} [data] - JSON body
     * @return {angular.IPromise<*>} `data` of the tastytrade response envelope
     * @private
     */
    function _request(path, params, method, data) {
      if (!gUserData) return $q.reject(new Error('Not logged in to tastytrade'));
      // Until the user logs in again, every poll would post another refresh that tastytrade refuses.
      if (gUserData === gRefusedSession) return $q.reject(new Error(SESSION_EXPIRED));

      const send = function () {
        return $http({
          method: method || 'GET',
          url: BASE_URLS[gUserData.mode] + path,
          params: params,
          data: data,
          headers: { Authorization: 'Bearer ' + gUserData.token, Accept: 'application/json' },
        });
      };

      const tokenPromise = Date.now() < gUserData.expiresAt - EXPIRY_MARGIN_MS ? $q.resolve() : _refreshToken();

      return tokenPromise
        .then(send)
        .catch(function (response) {
          if (response && response.status === 401) return _refreshToken().then(send);
          return $q.reject(response);
        })
        .then(function (response) {
          return response.data.data;
        })
        .catch(function (reason) {
          return $q.reject(_convertError(reason));
        });
    }

    /**
     * @return {angular.IPromise<void>}
     * @private
     */
    function _refreshToken() {
      // Concurrent polls share one refresh instead of each spending a token request.
      if (gRefreshPromise) return gRefreshPromise;

      const url = btSettings.BT_BACKEND_URL + '/auth/tastytrade/refresh';
      // A refresh that finishes after logout or a new login must not touch the new session.
      const session = gUserData;

      const promise = $http
        .post(url, { refreshToken: session.refreshToken }, { headers: { Authorization: LoopBackAuth.accessTokenId } })
        .then(function (response) {
          if (gUserData !== session) return $q.reject(new Error('Not logged in to tastytrade'));
          session.token = response.data.accessToken;
          session.expiresAt = _getExpiresAt(response.data.expiresIn);
          _saveUserData();
        })
        .catch(function (reason) {
          if (reason instanceof Error) return $q.reject(reason);
          // The backend answers 401 when tastytrade refused the grant; anything else is not a lost session.
          if (reason && reason.status === 401) {
            if (gUserData === session) gRefusedSession = session;
            return $q.reject(new Error(SESSION_EXPIRED));
          }
          return $q.reject(
            new Error(
              'tastytrade token refresh failed' + (reason && reason.status ? ' (HTTP ' + reason.status + ')' : '')
            )
          );
        })
        .finally(function () {
          if (gRefreshPromise === promise) gRefreshPromise = null;
        });

      gRefreshPromise = promise;
      return promise;
    }

    /**
     * @private
     */
    function _saveUserData() {
      if (gSaveData && gUserData) gSaveData(gUserData);
    }

    /**
     * @param {number} expiresIn - seconds
     * @return {number} timestamp in ms
     * @private
     */
    function _getExpiresAt(expiresIn) {
      return Date.now() + (expiresIn || 0) * 1000;
    }

    /**
     * @param {*} reason - Error, or $http response with tastytrade `{ error: { code, message, errors? } }` body
     * @return {Error}
     * @private
     */
    function _convertError(reason) {
      if (reason instanceof Error) return reason;

      const error = reason && reason.data && reason.data.error;
      if (error && error.message) {
        // Order rejections say only "One or more preflight checks failed"; the reasons are in `errors`.
        const details = (error.errors || []).filter(function (item) {
          return item && item.message;
        });
        if (!details.length) return new Error(error.message);
        return new Error(
          details
            .map(function (item) {
              return item.message + (ERROR_HINTS[item.code] ? ' ' + ERROR_HINTS[item.code] : '');
            })
            .join(' ')
        );
      }
      // A 429 comes back as an nginx HTML page, not the JSON error envelope.
      if (reason && reason.status === 429)
        return new Error('tastytrade rate limit reached - try again in a few seconds');
      if (reason && reason.status) return new Error('tastytrade request failed (HTTP ' + reason.status + ')');
      return new Error('tastytrade request failed');
    }

    /**
     * @param {Array<{ message?: string }>} [items] - tastytrade warnings or errors
     * @return {string}
     * @private
     */
    function _joinMessages(items) {
      return (items || [])
        .map(function (item) {
          return item.message;
        })
        .filter(Boolean)
        .join(' ');
    }

    /**
     * @param {string} ticker - `ST:AAPL` (BetterTrader stock ticker) or a bare `AAPL`
     * @return {string | null} tastytrade equity symbol, or null for forex, crypto and other non-stock tickers
     * @private
     */
    function _getStockSymbol(ticker) {
      const own = _getOwnSymbol(ticker);
      if (own) return own;

      const parts = String(ticker || '')
        .trim()
        .toUpperCase()
        .split(':');
      return parts.length === 2 && parts[0] === btSymbolPrefix.STOCK && parts[1] ? parts[1] : null;
    }

    /**
     * @param {string} ticker
     * @return {string | null} tastytrade equity symbol for `TASTYTRADE:AAPL` or bare `AAPL`, otherwise null
     * @private
     */
    function _getOwnSymbol(ticker) {
      const value = String(ticker || '')
        .trim()
        .toUpperCase();
      if (checkTastytradeTicker(value)) return value.slice(TICKER_PREFIX.length + 1) || null;
      return value && !btInstrumentsService.checkBetterTraderSymbol(value) ? value : null;
    }

    /**
     * Its `brokerSymbol` stays the bare ticker: that is what positions, orders and the watchlist store.
     *
     * @param {string} symbol - tastytrade equity symbol
     * @param {string} description
     * @param {Object} raw - tastytrade equity instrument
     * @return {ecapp.ITradingInstrument}
     * @private
     */
    function _createOwnInstrument(symbol, description, raw) {
      const instrument = btInstrumentsService.createBrokerInstrument(BROKER, symbol, BROKER, description, false, raw);
      // createBrokerInstrument returns its cached object untouched, which may be createInstrument's bare placeholder.
      if (raw && raw.symbol) {
        instrument.raw = raw;
        instrument.description = description;
      }
      // createBrokerInstrument defaults to 4 decimals; stocks tick in cents (the last tick size covers normal prices).
      const tickSizes = (instrument.raw && instrument.raw['tick-sizes']) || [];
      const tick = tickSizes.length ? String(tickSizes[tickSizes.length - 1].value) : '0.01';
      instrument.pip = tick;
      instrument.tick = parseFloat(tick);
      instrument.precision = (tick.split('.')[1] || '').length;
      return instrument;
    }

    /**
     * @param {{ symbol: string, description: string, 'instrument-type': string }} item
     * @return {ecapp.ITradingSymbolSuggestion}
     * @private
     */
    function _convertSearchResult(item) {
      return {
        // Bare, like the watchlist stores it, so the search marks it as already added.
        ticker: item.symbol,
        name: item.symbol,
        desc: item.description + ' (tastytrade)',
        type: 'Stock',
        rawData: item,
      };
    }

    /**
     * @param {Array<Array<*>>} arrays
     * @return {Array<*>}
     * @private
     */
    function _flatten(arrays) {
      return [].concat.apply([], arrays);
    }

    // --- Converters (tastytrade returns kebab-case keys and numbers as strings) ---

    /**
     * @param {{ account: Object<string, *>, 'authority-level': string }} item
     * @return {ecapp.ITradingAccount}
     * @private
     */
    function _convertAccount(item) {
      const number = item.account['account-number'];
      return {
        acc: number,
        key: number,
        name: item.account.nickname ? item.account.nickname + ' ' + number : number,
        type: item.account['margin-or-cash'],
        rawData: item,
      };
    }

    /**
     * @param {Object<string, *>} balance
     * @return {ecapp.ITradingBalance}
     * @private
     */
    function _convertBalance(balance) {
      const number = balance['account-number'];
      const account = gAccounts.find(function (item) {
        return item.key === number;
      });

      return {
        acc: number,
        key: number,
        name: account ? account.name : number,
        type: account ? account.type : '',
        NAV: _toNumber(balance['net-liquidating-value']),
        // Balances carry no P&L; it is per position (see _convertPosition).
        UPL: 0,
        RPL: 0,
        Balance: _toNumber(balance['cash-balance']),
        MarginUsed: _toNumber(balance['maintenance-requirement']),
        MarginAvailable: _toNumber(balance['equity-buying-power']),
        rawData: balance,
      };
    }

    /**
     * @param {Object<string, *>} position
     * @return {ecapp.ITradingPosition}
     * @private
     */
    function _convertPosition(position) {
      const quantity = _toNumber(position.quantity);
      const multiplier = _toNumber(position.multiplier) || 1;
      const avg = _toNumber(position['average-open-price']);
      const lastPrice = _toNumber(position['mark-price']) || _toNumber(position['close-price']);
      const sign = position['quantity-direction'] === 'Short' ? -1 : 1;
      const total = avg * quantity * multiplier;
      const pnl = (lastPrice - avg) * quantity * multiplier * sign;

      return {
        key: position['account-number'] + ':' + position.symbol,
        acc: position['account-number'],
        symbol: position.symbol,
        displayName: position.symbol,
        description: position['underlying-symbol'] || position.symbol,
        position: position['quantity-direction'],
        quantity: quantity,
        OPL: pnl,
        acct: total ? (pnl / total) * 100 : 0,
        total: total,
        margin: 0,
        avg: avg,
        mrkValue: lastPrice * quantity * multiplier,
        lastPrice: lastPrice,
        rawData: position,
      };
    }

    /**
     * @param {Object<string, *>} order
     * @return {*} same untyped shape as Tradier's; ITradingOrder wants methods and lacks `type`/`filled`
     * @private
     */
    function _convertOrder(order) {
      const legs = order.legs || [];
      const fills = _flatten(
        legs.map(function (leg) {
          return leg.fills || [];
        })
      );
      const filled = fills.reduce(function (sum, fill) {
        return sum + _toNumber(fill.quantity);
      }, 0);
      const action = legs.length ? legs[0].action : '';
      const type = order['order-type'];
      const price = _toNumber(order.price);
      const stop = _toNumber(order['stop-trigger']);
      const known = ORDER_STATUSES[order.status];
      // Still working, so it keeps the open status that btTradingService.hasOrders and the Open tab look for.
      const isPartiallyFilled = order.status === 'Live' && filled > 0;

      return {
        key: String(order.id),
        acc: order['account-number'],
        symbol: order['underlying-symbol'],
        displayName: order['underlying-symbol'],
        quantity: _toNumber(order.size),
        side: action.indexOf('Buy') === 0 ? 'Long' : 'Short',
        action: action,
        // undefined when absent, like TradeStation: bt-open-orders-widget.html labels any defined `stop` a Stop order.
        limit: type === 'Limit' || type === 'Stop Limit' || type === 'Marketable Limit' ? price : undefined,
        stop: stop || undefined,
        price: price || stop,
        type: type === 'Stop Limit' ? 'Stop/Limit' : type,
        status: known ? known.status : order.status,
        statusText: isPartiallyFilled ? 'Partially filled' : known ? known.text : order.status,
        reason: order['reject-reason'],
        fillPrice: fills.length ? _toNumber(fills[fills.length - 1]['fill-price']) : 0,
        filled: filled,
        placeTime: order['received-at'],
        executeTime: order['terminal-at'],
        rawData: order,
      };
    }

    /**
     * @param {*} value - tastytrade numeric string
     * @return {number}
     * @private
     */
    function _toNumber(value) {
      const number = parseFloat(value);
      return isNaN(number) ? 0 : number;
    }

    const service = {
      initialize: initialize,
      getAccessData: getAccessData,

      connect: connect,
      disconnect: logout,

      login: login,
      fastLogin: comingSoon,
      logout: logout,
      signUp: comingSoon,
      checkUser: comingSoon,
      getUsername: comingSoon,
      isLoggedIn: isLoggedIn,

      getTradingMode: getTradingMode,
      setTradingMode: setTradingMode,

      getAccounts: getAccounts,
      getBalances: getBalances,
      getPositions: getPositions,
      getOrders: getOrders,

      selectAccount: selectAccount,
      isAccountSelected: isAccountSelected,
      getSelectedAccountId: getSelectedAccountId,

      getSymbolInfo: getSymbolInfo,
      searchSymbol: btMarketApiService.searchSymbol,
      suggestSymbols: suggestSymbols,
      getQuotes: getQuotes,
      streamQuote: btMarketApiService.streamQuote,
      getSnapshots: btMarketApiService.getSnapshots,
      streamSnapshot: btMarketApiService.streamSnapshot,
      getLiveCandleData: getLiveCandleData,
      getLiveCandlesData: getLiveCandlesData,
      getCandles: btMarketApiService.getCandles,
      getLastCandlesData: getLastCandlesData,
      getEntryPrice: btMarketApiService.getEntryPrice,

      confirmOrder: confirmOrder,
      submitOrder: submitOrder,
      updateOrder: comingSoon,
      cancelOrder: cancelOrder,

      checkTradable: checkTradable,
      checkSameSymbol: checkSameSymbol,
      checkTastytradeTicker: checkTastytradeTicker,
      checkLoginEnabled: checkLoginEnabled,
      createDatafeed: createDatafeed,
      createInstrument: createInstrument,
      getDxLink: getDxLink,
    };
    return service;
  }
})();
